import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { MAGNIFLEX_LOGO_URL, MAGNIFLEX_TAGLINE } from "@/lib/brand";
import { toast } from "sonner";
import { Lock, Mail, ArrowRight } from "lucide-react";

function LoginShell({ title, subtitle, role, redirect, testid, otherRoleHref, otherRoleLabel }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email, password);
      if (u.role !== role) {
        setError(`Questo account non ha il ruolo richiesto (${role}).`);
        setLoading(false);
        return;
      }
      toast.success("Accesso effettuato");
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid={testid}>
      {/* Left visual panel */}
      <div className="hidden md:flex md:w-1/2 bg-[#003D5C] text-white p-12 lg:p-16 flex-col justify-between relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-[#E30613]" />
        <div>
          <img
            src={MAGNIFLEX_LOGO_URL}
            alt="Magniflex"
            className="h-14 brightness-0 invert"
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <div className="mt-2 text-[11px] uppercase tracking-[0.3em] text-white/70">Hospitality Platform</div>
        </div>

        <div>
          <div className="mgx-divider-accent mb-8" />
          <h2 className="mgx-display text-4xl lg:text-5xl text-white leading-[1.05]">
            {MAGNIFLEX_TAGLINE}
          </h2>
          <p className="mt-6 text-white/70 text-sm leading-relaxed max-w-md">
            La piattaforma riservata al team commerciale Magniflex per gestire hotel partner, prodotti, coupon e ordini con match automatico.
          </p>
        </div>

        <div className="text-[11px] uppercase tracking-[0.3em] text-white/50">
          Magniflex — Made in Italy dal 1962
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="md:hidden text-center mb-10">
            <img src={MAGNIFLEX_LOGO_URL} alt="Magniflex" className="h-12 mx-auto" />
            <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-[#6B7280]">Hospitality Platform</div>
          </div>

          <div className="mb-2">
            <div className="mgx-divider-accent mb-6" />
            <h1 className="mgx-display text-3xl md:text-4xl text-[#003D5C]">{title}</h1>
            <p className="text-sm text-[#6B7280] mt-2">{subtitle}</p>
          </div>

          <form onSubmit={submit} className="space-y-5 mt-8">
            <div>
              <label className="mgx-label flex items-center gap-2 mb-2">
                <Mail size={11} /> Email
              </label>
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mgx-input"
                placeholder="nome@magniflex.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="mgx-label flex items-center gap-2 mb-2">
                <Lock size={11} /> Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mgx-input"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="border border-[#E30613]/30 bg-[#E30613]/5 text-[#B40510] text-sm px-4 py-3 rounded-sm" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="mgx-btn-primary w-full"
              data-testid="login-submit-btn"
            >
              {loading ? "Accesso..." : <>Accedi <ArrowRight size={14} className="ml-2" /></>}
            </button>
          </form>

          {otherRoleHref && (
            <div className="text-center text-xs text-[#6B7280] mt-8">
              <Link to={otherRoleHref} className="hover:text-[#003D5C] transition-colors uppercase tracking-[0.2em]">
                {otherRoleLabel} →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginShell;
